import { useRef } from 'react';
import { Archive, Loader2, Pencil, Save, Settings2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface CapabilityHeaderActionsProps {
   typeLabel: string;
   hasWorkingRevision: boolean;
   isViewingPublished: boolean;
   isArchived: boolean;
   selectedPublishedLabel: string | null;
   isDirty: boolean;
   isSaving: boolean;
   isPublishing: boolean;
   onSave: () => void;
   onPublish: () => void;
   onEdit: () => void;
   onArchive: () => void;
   onSettings: () => void;
}

export function CapabilityHeaderActions({
   typeLabel,
   hasWorkingRevision,
   isViewingPublished,
   isArchived,
   selectedPublishedLabel,
   isDirty,
   isSaving,
   isPublishing,
   onSave,
   onPublish,
   onEdit,
   onArchive,
   onSettings,
}: CapabilityHeaderActionsProps) {
   const saveRef = useRef(onSave);
   saveRef.current = onSave;
   const publishRef = useRef(onPublish);
   publishRef.current = onPublish;
   const editRef = useRef(onEdit);
   editRef.current = onEdit;

   if (isArchived) return null;

   return (
      <div className="flex items-center gap-2">
         <Button
            variant="outline"
            size="sm"
            onClick={onSettings}
            className="inline-flex items-center gap-1.5"
         >
            <Settings2 size={14} />
            Settings
         </Button>
         {hasWorkingRevision && !isViewingPublished ? (
            <>
               <Button
                  type="button"
                  variant="default"
                  size="sm"
                  onClick={() => void saveRef.current()}
                  disabled={!isDirty || isSaving}
                  className="inline-flex items-center gap-1.5"
               >
                  {isSaving ? (
                     <Loader2 size={14} className="animate-spin" />
                  ) : (
                     <Save size={14} />
                  )}
                  Save
               </Button>
               <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => void publishRef.current()}
                  disabled={isPublishing}
                  className="inline-flex items-center gap-1.5"
               >
                  {isPublishing ? (
                     <Loader2 size={14} className="animate-spin" />
                  ) : (
                     <Upload size={14} />
                  )}
                  Publish
               </Button>
            </>
         ) : isViewingPublished ? (
            <Button
               type="button"
               variant="default"
               size="sm"
               onClick={() => void editRef.current()}
               className="inline-flex items-center gap-1.5"
            >
               <Pencil size={14} />
               Edit{selectedPublishedLabel ? ` from ${selectedPublishedLabel}` : ''}
            </Button>
         ) : !hasWorkingRevision ? (
            <Button
               type="button"
               variant="default"
               size="sm"
               onClick={() => void editRef.current()}
               className="inline-flex items-center gap-1.5"
            >
               <Pencil size={14} />
               Edit
            </Button>
         ) : null}
         <Button
            type="button"
            variant="destructive"
            size="sm"
            onClick={onArchive}
            className="inline-flex items-center gap-1.5"
         >
            <Archive size={14} />
            Archive {typeLabel}
         </Button>
      </div>
   );
}
